import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import debounce from 'lodash/debounce'
import styled from 'styled-components'
import { rem } from 'polished'
import DualSlider from 'LuxKit/Slider/DualSlider'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { useAppSelector } from 'hooks/reduxHooks'
import Input from 'components/Common/Form/Input/Input'
import ExperienceSearchFilterDraftContext from 'contexts/Experience/ExperienceSearchFilterDraftContext'

const PriceInputWrap = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(12)};
  flex: 1;
  min-width: 0;
  direction: ltr;

  &:focus-within {
    border-color: ${(props) => props.theme.palette.neutral.default.two};
  }
`

const PriceInput = styled(Input)`
  border: none;
  outline: none;
  background: transparent;
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  width: 100%;
  min-width: 0;
  padding: 0;
`

interface Props {
  place?: App.Place;
  listIds?: Array<string>;
}

function ExperienceSearchPriceFilter({ place, listIds }: Props) {
  const { searchFilters, setPriceGteSearchFilter, setPriceLteSearchFilter } =
    useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const isDraft = !!useContext(ExperienceSearchFilterDraftContext)

  const { priceRange } = useMostPopulatedSearchExperienceFacets(place)

  const experiencesById = useAppSelector((state) => state.experience.experiences)
  const loadedMaxPrice = useMemo(() => {
    return (listIds ?? []).reduce((max, id) => {
      const price = experiencesById[id]?.price ?? 0
      return price > max ? price : max
    }, 0)
  }, [listIds, experiencesById])
  const currencyFormatter = useCurrencyFormatter()

  const slider = useMemo(() => {
    const hasCategoryFilter = (searchFilters.categoryCodes?.length ?? 0) > 0
    const globalMax = priceRange?.max ?? 0
    if (globalMax > 0 || loadedMaxPrice > 0) {
      const effectiveMax = hasCategoryFilter && loadedMaxPrice > 0
        ? loadedMaxPrice
        : Math.max(globalMax, loadedMaxPrice)
      if (effectiveMax <= 0) return undefined
      const { min: pmin } = priceRange ?? { min: 0 }
      const step = effectiveMax <= 10000 ? 10 : 100
      const min = Math.floor(Math.max(0, pmin) / step) * step
      const max = Math.ceil(effectiveMax / step) * step
      return { min, max, step }
    }
  }, [priceRange, loadedMaxPrice, searchFilters.categoryCodes])

  const [startValue, setStartValue] = useState<number | undefined>(
    () => searchFilters.priceGte ?? slider?.min,
  )
  const [endValue, setEndValue] = useState<number | undefined>(
    () => searchFilters.priceLte ?? slider?.max,
  )

  useEffect(() => {
    setStartValue(searchFilters.priceGte ?? slider?.min)
  }, [searchFilters.priceGte, slider?.min])

  useEffect(() => {
    setEndValue(searchFilters.priceLte ?? slider?.max)
  }, [searchFilters.priceLte, slider?.max])

  const priceGteRef = useRef(setPriceGteSearchFilter)
  priceGteRef.current = setPriceGteSearchFilter
  const priceLteRef = useRef(setPriceLteSearchFilter)
  priceLteRef.current = setPriceLteSearchFilter
  const trackSearchFilterRef = useRef(trackSearchFilter)
  trackSearchFilterRef.current = trackSearchFilter

  const debouncedSetPriceGte = useMemo(
    () =>
      debounce((value: number) => {
        trackSearchFilterRef.current({
          label: 'Minimum price',
          type: 'price',
          value,
        })
        priceGteRef.current(value)
      }, 500),
    [],
  )

  const debouncedSetPriceLte = useMemo(
    () =>
      debounce((value: number) => {
        trackSearchFilterRef.current({
          label: 'Maximum price',
          type: 'price',
          value,
        })
        priceLteRef.current(value)
      }, 500),
    [],
  )

  const debouncedInputCommitMin = useMemo(
    () =>
      debounce((value: number) => {
        trackSearchFilterRef.current({
          label: 'Minimum price',
          type: 'price',
          value,
        })
        priceGteRef.current(value)
      }, 2000),
    [],
  )

  const debouncedInputCommitMax = useMemo(
    () =>
      debounce((value: number) => {
        trackSearchFilterRef.current({
          label: 'Maximum price',
          type: 'price',
          value,
        })
        priceLteRef.current(value)
      }, 2000),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSetPriceGte.cancel()
      debouncedSetPriceLte.cancel()
      debouncedInputCommitMin.cancel()
      debouncedInputCommitMax.cancel()
    }
  }, [debouncedSetPriceGte, debouncedSetPriceLte, debouncedInputCommitMin, debouncedInputCommitMax])

  const onSliderStartChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (e) => {
      const value = e.currentTarget.valueAsNumber
      setStartValue(value)
      if (!inputMinFocused.current) setInputMinStr(String(value))
      if (isDraft) setPriceGteSearchFilter(value)
      else debouncedSetPriceGte(value)
    },
    [debouncedSetPriceGte, isDraft, setPriceGteSearchFilter],
  )

  const onSliderEndChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (e) => {
      const value = e.currentTarget.valueAsNumber
      setEndValue(value)
      if (!inputMaxFocused.current) setInputMaxStr(String(value))
      if (isDraft) setPriceLteSearchFilter(value)
      else debouncedSetPriceLte(value)
    },
    [debouncedSetPriceLte, isDraft, setPriceLteSearchFilter],
  )

  const [inputMinStr, setInputMinStr] = useState<string>(() =>
    String(searchFilters.priceGte ?? slider?.min ?? 0),
  )
  const [inputMaxStr, setInputMaxStr] = useState<string>(() =>
    String(searchFilters.priceLte ?? slider?.max ?? 0),
  )
  const inputMinFocused = useRef(false)
  const inputMaxFocused = useRef(false)

  useEffect(() => {
    if (!inputMinFocused.current) {
      setInputMinStr(String(searchFilters.priceGte ?? slider?.min ?? 0))
    }
  }, [searchFilters.priceGte, slider?.min])

  useEffect(() => {
    if (!inputMaxFocused.current) {
      setInputMaxStr(String(searchFilters.priceLte ?? slider?.max ?? 0))
    }
  }, [searchFilters.priceLte, slider?.max])

  const commitMin = useCallback(() => {
    const sliderMin = slider?.min ?? 0
    const sliderMax = slider?.max ?? Infinity
    const currentMax = searchFilters.priceLte ?? sliderMax
    const parsed = parseInt(inputMinStr, 10)

    if (isNaN(parsed)) {
      setInputMinStr(String(searchFilters.priceGte ?? sliderMin))
      return
    }

    const clamped = Math.max(sliderMin, Math.min(parsed, currentMax))
    setStartValue(clamped)
    debouncedSetPriceGte.cancel()
    debouncedInputCommitMin.cancel()
    trackSearchFilter({
      label: 'Minimum price',
      type: 'price',
      value: clamped,
    })
    setPriceGteSearchFilter(clamped)
    setInputMinStr(String(clamped))
  }, [
    inputMinStr,
    slider,
    searchFilters.priceGte,
    searchFilters.priceLte,
    setPriceGteSearchFilter,
    trackSearchFilter,
    debouncedSetPriceGte,
    debouncedInputCommitMin,
  ])

  const commitMax = useCallback(() => {
    const sliderMin = slider?.min ?? 0
    const sliderMax = slider?.max ?? Infinity
    const currentMin = searchFilters.priceGte ?? sliderMin
    const parsed = parseInt(inputMaxStr, 10)

    if (isNaN(parsed)) {
      setInputMaxStr(String(searchFilters.priceLte ?? sliderMax))
      return
    }

    const clamped = Math.min(sliderMax, Math.max(parsed, currentMin))
    setEndValue(clamped)
    debouncedSetPriceLte.cancel()
    debouncedInputCommitMax.cancel()
    trackSearchFilter({
      label: 'Maximum price',
      type: 'price',
      value: clamped,
    })
    setPriceLteSearchFilter(clamped)
    setInputMaxStr(String(clamped))
  }, [
    inputMaxStr,
    slider,
    searchFilters.priceGte,
    searchFilters.priceLte,
    setPriceLteSearchFilter,
    trackSearchFilter,
    debouncedSetPriceLte,
    debouncedInputCommitMax,
  ])

  const onInputStartChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >((e) => {
    const raw = e.currentTarget.value.replace(/[^0-9]/g, '')
    setInputMinStr(raw)
    const parsed = parseInt(raw, 10)
    if (!isNaN(parsed) && slider && parsed >= slider.min && parsed <= (endValue ?? slider.max)) {
      debouncedInputCommitMin(parsed)
    }
  }, [debouncedInputCommitMin, slider, endValue])

  const onInputEndChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >((e) => {
    const raw = e.currentTarget.value.replace(/[^0-9]/g, '')
    setInputMaxStr(raw)
    const parsed = parseInt(raw, 10)
    if (!isNaN(parsed) && slider && parsed <= slider.max && parsed >= (startValue ?? slider.min)) {
      debouncedInputCommitMax(parsed)
    }
  }, [debouncedInputCommitMax, slider, startValue])

  const onInputStartKeyDown = useCallback<
    React.KeyboardEventHandler<HTMLInputElement>
  >(
    (e) => {
      if (e.key === 'Enter') {
        commitMin()
        e.currentTarget.blur()
      }
    },
    [commitMin],
  )

  const onInputEndKeyDown = useCallback<
    React.KeyboardEventHandler<HTMLInputElement>
  >(
    (e) => {
      if (e.key === 'Enter') {
        commitMax()
        e.currentTarget.blur()
      }
    },
    [commitMax],
  )

  const currencySymbol = useMemo(() => {
    const formatted = currencyFormatter(0, 'roundedDollar')
    return formatted?.replace(/[\d.,\s‎‏]/g, '') ?? ''
  }, [currencyFormatter])

  if (!slider) return null

  const effectiveStart = startValue ?? slider.min
  const effectiveEnd = endValue ?? slider.max

  return (
    <VerticalSpacer gap={16}>
      <DualSlider
        min={slider.min}
        max={slider.max}
        step={slider.step}
        defaultStartValue={searchFilters.priceGte ?? slider.min}
        defaultEndValue={searchFilters.priceLte ?? slider.max}
        startValue={effectiveStart}
        endValue={effectiveEnd}
        onStartChange={onSliderStartChange}
        onEndChange={onSliderEndChange}
      />
      <Group direction="horizontal" gap={8} horizontalAlign="stretch">
        <PriceInputWrap>
          <BodyText variant="medium" as="span">
            {currencySymbol}
          </BodyText>
          <PriceInput
            type="text"
            value={inputMinStr}
            onChange={onInputStartChange}
            onKeyDown={onInputStartKeyDown}
            onFocus={() => {
              inputMinFocused.current = true
            }}
            onBlur={() => {
              inputMinFocused.current = false
              commitMin()
            }}
            inputMode="numeric"
          />
        </PriceInputWrap>
        <PriceInputWrap>
          <BodyText variant="medium" as="span">
            {currencySymbol}
          </BodyText>
          <PriceInput
            type="text"
            value={inputMaxStr}
            onChange={onInputEndChange}
            onKeyDown={onInputEndKeyDown}
            onFocus={() => {
              inputMaxFocused.current = true
            }}
            onBlur={() => {
              inputMaxFocused.current = false
              commitMax()
            }}
            inputMode="numeric"
          />
        </PriceInputWrap>
      </Group>
    </VerticalSpacer>
  )
}

export default ExperienceSearchPriceFilter
